import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Tenant } from '../lib/tenant';
import { quadAspect, type QuadPoints } from '../render/homography';
import { lightingFromCanvas } from '../render/lighting';
import { computeRepeats, FloorRenderer, webgl2Supported } from '../render/renderer';
import { stemProfile, wallContacts } from '../render/stemWall';
import { watermarkRect, type WatermarkRect } from '../render/watermark';
import type { SpaceType } from '../shared/schemas';
import { selection, type Session } from './session';

/**
 * Owns the WebGL renderer for the editor canvas and redraws whenever the
 * photo, mask or choices change. Color changes only swap a cached texture.
 */
export function useFloorRenderer(tenant: Tenant, session: Session, showOriginal = false) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<FloorRenderer | null>(null);
  const last = useRef<{ photo?: HTMLCanvasElement; mask?: Uint8Array | null; wmImage?: ImageBitmap }>({});
  const [unsupported] = useState(() => !webgl2Supported());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (unsupported || !canvasRef.current) return;
    try {
      rendererRef.current = new FloorRenderer(canvasRef.current);
    } catch (err) {
      console.error(err);
      setError('webgl');
    }
    return () => {
      rendererRef.current?.destroy();
      rendererRef.current = null;
      last.current = {};
    };
  }, [unsupported]);

  const { photo, mask } = session;
  const { system, color, spaceType } = selection(tenant.config, session);
  const textureUrl = tenant.asset(color.texture);
  const aspect = floorAspect(session.quad, photo, spaceType);
  const [rx, ry] = computeRepeats(spaceType.widthFt, aspect, system.tileInches, session.patternScale);
  const stemCfg = tenant.config.stemWall;
  const { wallMask } = session;
  // Where the floor meets walls; only depends on the masks, so drags in Adjust stay cheap.
  const contacts = useMemo(
    () => (stemCfg && mask && wallMask ? wallContacts(mask, wallMask, photo.width, photo.height) : null),
    [stemCfg, mask, wallMask, photo],
  );
  const stemOn = !!contacts && session.stemWall;
  const wmCfg = tenant.config.watermark;
  const watermark = useWatermark(tenant, mask, photo);

  useEffect(() => {
    const r = rendererRef.current;
    if (!r) return;
    const w = photo.width;
    const h = photo.height;
    if (last.current.photo !== photo) {
      r.setPhoto(photo);
      r.fitTo(displayLongEdge(r.canvas));
      last.current = { photo };
    }
    if (!('mask' in last.current) || last.current.mask !== mask) {
      const m = mask ?? new Uint8Array(w * h);
      r.setMask(m, w, h);
      r.setLighting(lightingFromCanvas(photo, m, w, h));
      last.current.mask = mask;
    }
    const widthIn = spaceType.widthFt * 12;
    if (watermark && last.current.wmImage !== watermark.image) {
      r.setWatermarkImage(watermark.image);
      last.current.wmImage = watermark.image;
    }
    r.setWatermarkPlacement(watermark && wmCfg ? { rect: watermark.rect, opacity: wmCfg.opacity } : null);
    r.setStem(stemOn ? stemProfile(contacts!, w, h, session.quad, widthIn, widthIn * aspect, stemCfg?.heightInches ?? 4) : null);
    r.setParams({
      quad: session.quad,
      repeats: [rx, ry],
      tileIn: system.tileInches * session.patternScale,
      finish: system.finish,
      wrap: system.wrap,
      showOriginal: showOriginal || !mask,
    });

    let cancelled = false;
    r.loadTexture(textureUrl, system.wrap)
      .then((tex) => {
        if (cancelled) return;
        r.setTexture(tex);
        r.draw();
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError('texture');
      });
    return () => {
      cancelled = true;
    };
  }, [photo, mask, textureUrl, system.wrap, session.quad, rx, ry, system.finish, showOriginal, stemOn, contacts, aspect, watermark]);

  // Render at the displayed resolution: re-fit when the layout or window changes.
  useEffect(() => {
    const r = rendererRef.current;
    if (!r || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (r.fitTo(displayLongEdge(r.canvas))) r.draw();
    });
    ro.observe(r.canvas);
    return () => ro.disconnect();
  }, [unsupported]);

  // Warm the cache for the rest of the current system's colors.
  useEffect(() => {
    const r = rendererRef.current;
    if (!r || !mask) return;
    const id = setTimeout(() => system.colors.forEach((c) => r.loadTexture(tenant.asset(c.texture), system.wrap).catch(() => {})), 300);
    return () => clearTimeout(id);
  }, [system.id, !!mask]);

  return { canvasRef, rendererRef, unsupported: unsupported || error === 'webgl', error, stemAvailable: !!contacts && contacts.contact.some((c) => c >= 0), watermark: watermark && wmCfg ? { ...watermark, opacity: wmCfg.opacity } : null };
}

/** Render long edge: the canvas as shown on screen in device pixels, supersampled 2x. */
function displayLongEdge(canvas: HTMLCanvasElement) {
  const { width, height } = canvas.getBoundingClientRect();
  // 2x supersampling: crisper chips than rendering 1:1, without the grain of a bigger downscale.
  return Math.round(Math.max(width, height) * (window.devicePixelRatio || 1) * 2);
}

/** Depth / width of the floor area the quad covers; falls back to the space type's proportions. */
function floorAspect(quad: QuadPoints, photo: HTMLCanvasElement, spaceType: SpaceType) {
  try {
    const a = quadAspect(quad, photo.width, photo.height);
    if (Number.isFinite(a)) return Math.min(20, Math.max(0.05, a));
  } catch {
    // Degenerate quad: the renderer skips drawing anyway.
  }
  return spaceType.depthFt / spaceType.widthFt;
}

/** The tenant's watermark image (premultiplied, for WebGL and 2D canvas alike) and its place on this photo. */
function useWatermark(tenant: Tenant, mask: Uint8Array | null, photo: HTMLCanvasElement) {
  const cfg = tenant.config.watermark;
  const [image, setImage] = useState<ImageBitmap | null>(null);
  useEffect(() => {
    if (!cfg) return;
    let alive = true;
    fetch(tenant.asset(cfg.image))
      .then((r) => r.blob())
      .then((b) => createImageBitmap(b, { premultiplyAlpha: 'premultiply' }))
      .then((bmp) => alive && setImage(bmp))
      .catch((err) => console.error('watermark', err));
    return () => {
      alive = false;
    };
  }, [cfg?.image]);
  return useMemo<{ image: ImageBitmap; rect: WatermarkRect } | null>(
    () => (cfg && image ? { image, rect: watermarkRect(mask, photo.width, photo.height, image.width / image.height, cfg.size) } : null),
    [cfg, image, mask, photo],
  );
}
